import { APIS, isNewBackend, isProduction } from 'config';
import { GraphQLClient, RequestDocument, Variables } from 'graphql-request';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { currentContextSiteSelector } from 'store/selectors/currentContextSiteSelector';
import useSWR, { SWRConfiguration } from 'swr';
import useCurrentSelectedSite from './useCurrentSelectedSite';

const graphQLClient = (
  siteId?: string,
  options?: {
    headers?: {
      [key: string]: string;
    };
  }
) =>
  new GraphQLClient(APIS.GQL, {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'cynet-site-id': siteId ?? '',
      ...options?.headers
    }
  });

type VariablesWithSiteId = {
  siteId?: string;
} & { variables: Variables };

export type ConfigWithUnsentVariables = {
  unsentVariables?: Record<string, any>;
  triggerWithoutContextSiteGuid?: boolean;
} & SWRConfiguration;

const fetcher = (
  query: RequestDocument,
  variablesWithSiteId: VariablesWithSiteId | undefined,
  headers: {
    [key: string]: string;
  }
) =>
  graphQLClient(variablesWithSiteId?.siteId, {
    headers
  }).request(query, variablesWithSiteId?.variables);

export const useGetSiteId = () => {
  const { site } = useCurrentSelectedSite();
  const clientId = useMemo(() => {
    if (!isProduction) {
      return process.env.REACT_APP_SITE_ID ?? site?.ClientId;
    }
    return site?.ClientId;
  }, [site?.ClientId]);
  return clientId?.toString();
};

const useGql = <T = unknown>(
  query: string,
  variables: Variables = {},
  { unsentVariables, triggerWithoutContextSiteGuid, ...config }: ConfigWithUnsentVariables = {},
  headers: {
    [key: string]: string;
  } = {}
) => {
  const currentData = useRef<T>();
  const siteId = useGetSiteId();
  const contextSite = useSelector(currentContextSiteSelector);
  const { isValidating, mutate, data, error } = useSWR<T>(
    [
      query,
      {
        variables: {
          ...variables,
          siteGuid: contextSite?.DbTenantId
        },
        siteId,
        ...unsentVariables
      },
      headers
    ],
    fetcher,
    {
      ...config,
      isPaused: () =>
        (!contextSite?.DbTenantId && !triggerWithoutContextSiteGuid) || Boolean(config?.isPaused?.()) || !isNewBackend
    }
  );

  if (data) {
    currentData.current = data;
  }

  return {
    data: currentData.current,
    isValidating,
    mutate,
    error,
    isLoading: !data && !error
  };
};

export default useGql;
